import { SelectionModel } from "@angular/cdk/collections";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { FileLogger } from "@comunicare/comunicare-helper";
import {
  DataType,
  GroupResponse,
  IDateInterval,
  IFilter,
  INPUT_TYPE,
  IPatientUser,
  IQuestionnaireList,
  IReference,
  ITableParameter,
  PreferenceContext,
  QuestionnaireScoring,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { BehaviorSubject, Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { Item } from "src/app/components/item-selector/item-selector.component";
import { PDF_TYPE_CONTEXT, PdfTypeChoiceComponent } from "src/app/components/patient-QR/pdf-type-choice/pdf-type-choice.component";
import { QRDetailsComponent } from "src/app/components/patient-QR/qr-details/qr-details.component";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { HelpData } from "src/app/helpers/helpData";
import { QuestionnaireListCSVHelper } from "src/app/helpers/QuestionnaireListCSVHelper";
import { QuestionnairePDFHelper } from "src/app/helpers/QuestionnairePDFHelper";
import { QuestionnairePDFHelperCheckBox } from "src/app/helpers/QuestionnairePDFHelperCheckBox";
import { Scoring } from "src/app/models/questionnaireScoring.model";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PatientService } from "src/app/providers/patient.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { QRService } from "src/app/providers/qr-api.service";
import { QuestionnairesService } from "src/app/providers/questionnaires.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { ToolsService } from "src/app/providers/tools.service";
import { UserService } from "src/app/providers/user.service";
import { UserStatisticsService } from "src/app/providers/userStatistics.service";
import { MobileQuestionnaireListSettingsComponent } from "./mobile-questionnaire-list-settings/mobile-questionnaire-list-settings.component";
import { QuestionnaireListDataSource } from "./questionnaire-list-datasource";
import { GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
@Component({
  selector: "app-questionnaire-list-page",
  templateUrl: "./questionnaire-list-page.component.html",
  styleUrls: ["./questionnaire-list-page.component.scss", "../../../assets/comunicare-list.scss"],
})
export class QuestionnaireListPageComponent implements OnInit, OnDestroy {
  /**
   * Material table
   */
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatTable) table: MatTable<IQuestionnaireList>;
  public dataSource: QuestionnaireListDataSource;
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public currentPageSize: number;
  // public defaultSort: MatSortable = {
  //     id: 'questionnaireDate',
  //     start: 'desc',
  //     disableClear: true
  // };
  public actionOnMultiple = false;
  public isAuthorizeShowAnonymousQr = false;
  public showAnonymized = false;

  // Columns

  /** List of all columns present in the template */
  readonly availableColumns = [
    "name",
    "firstname",
    "birthday",
    "gender",
    "questionnaireName",
    "questionnaireDate",
    "questionnaireScore",
    "action",
  ];
  public availableColumnItems: Item[] = [];
  public maxFromDate: moment.Moment;
  public minToDate: moment.Moment;
  public displayedColumns: string[];

  public fromDate: moment.Moment;
  public toDate: moment.Moment;

  /**
   * IFilter
   */
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;
  public globalSearchValue = "";

  /**
   * Services and organizations
   */

  public availableOrganizations: IReference[];
  public availableServices: IReference[];
  /**
   * Selection
   */
  public selection = new SelectionModel<IQuestionnaireList>(true, []);

  public exportPdfInProgressMap: { [id: string]: boolean } = {};
  public exportCsvInProgress: boolean;

  public today = moment();

  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match
  private onDestroy$ = new Subject<void>();
  public data$ = new BehaviorSubject<IQuestionnaireList[]>([]);

  constructor(
    public translateService: TranslateService,
    private preferenceService: PreferenceService,
    public helpData: HelpData,
    private qrService: QRService,
    private questionnaireService: QuestionnairesService,
    private sessionService: SessionService,
    private responsiveDialog: ResponsiveDialogService,
    private toolsService: ToolsService,
    private snackBar: MatSnackBar,
    private qPdfHelper: QuestionnairePDFHelper,
    private qPdfHelperCheckBox: QuestionnairePDFHelperCheckBox,
    private patientService: PatientService,
    private userService: UserService,
    private healthcareService: HealthcareserviceService,
    private questionnaireListCSVHelper: QuestionnaireListCSVHelper,
    private statService: UserStatisticsService,
    public responsiveService: ResponsiveService,
    private _bottomSheet: MatBottomSheet
  ) {
    this.maxFromDate = moment().endOf("day");
    this.minToDate = moment().subtract(3, "month");
    this.fromDate = this.minToDate;
    this.toDate = this.maxFromDate;
    this.dataSource = new QuestionnaireListDataSource(this.qrService, this.sessionService, this.userService, this.healthcareService);
    this.getDisplayedColumns();
  }

  ngOnInit(): void {
    this.isAuthorizeShowAnonymized();
    this.loadPreferences().then(() => {
      // Define all available columns and translate display
      this.translateService
        .get([
          "model.patient.name",
          "model.patient.firstname",
          "model.patient.birthdate",
          "model.patient.gender",
          "model.patient.questionnaireName",
          "model.patient.questionnaireDate",
          "model.patient.questionnaireScore",
          "table.action",
        ])
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((trans: string[]) => {
          this.availableColumnItems = [
            {
              value: "gender",
              display: trans["model.patient.gender"],
              checked: true,
            },
            {
              value: "name",
              display: trans["model.patient.name"],
              checked: true,
            },
            {
              value: "firstname",
              display: trans["model.patient.firstname"],
              checked: true,
            },
            {
              value: "birthday",
              display: trans["model.patient.birthdate"],
              checked: true,
            },
            {
              value: "questionnaireName",
              display: trans["model.patient.questionnaireName"],
              checked: true,
            },
            {
              value: "questionnaireDate",
              display: trans["model.patient.questionnaireDate"],
              checked: true,
            },
            {
              value: "questionnaireScore",
              display: trans["model.patient.questionnaireScore"],
              checked: true,
            },
            { value: "action", display: trans["table.action"], checked: true },
          ];
        });

      this.loadPreferences();

      this.setupServicesWatch();

      this.init(this.fromDate, this.toDate);
    });
  }

  ngOnDestroy(): void {
    this.dataSource.clear();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public initPaginatorAndSort(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;
    this.data$ = this.dataSource.connect();

    // Detect page size change
    this.paginator.page.pipe(takeUntil(this.onDestroy$)).subscribe((page) => {
      if (this.currentPageSize !== page.pageSize) {
        this.updatePreference();
      }
    });
  }

  public onDateChange(event: IDateInterval): void {
    this.fromDate = event.fromDate;
    this.toDate = event.toDate;

    this.init(this.fromDate, this.toDate);
  }

  private setupServicesWatch() {
    this.init(this.fromDate, this.toDate);
    this.initPaginatorAndSort();
    if (this.userService.isMonitoringUser) {
      this.sessionService.refreshCurrentMonitService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.init(this.fromDate, this.toDate);
        this.initPaginatorAndSort();
      });
    }
    this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.init(this.fromDate, this.toDate);
      this.initPaginatorAndSort();
    });
  }

  private init(from: moment.Moment, to: moment.Moment) {
    if (this.responsiveService.isMobile) {
      this.dataSource.clearFilter(); // remove filter on mobile
    }

    this.userService
      .isAuthorized("dashboard/questionnaireList", "GET")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isAuth) => {
        if (isAuth) {
          // wait for the end of changeDetection cycle with setTimeout to avoid "ExpressionChangedAfterItHasBeenCheckedError"
          setTimeout(() => {
            this.dataSource.loadQuestionnaires(from.toISOString(), to.toISOString(), this.showAnonymized);
          });
        }
      });
  }

  public availableColumnChanged(_items: Item[]): void {
    this.updatePreference();
  }

  /**
   * Drag n'Drop
   */
  public columnDropped(event: CdkDragDrop<string[]>): void {
    const realIndexFrom =
      this.availableColumnItems.findIndex((item) => this.displayedColumns[event.previousIndex] === item.value) +
      (this.actionOnMultiple ? 1 : 0);
    const realIndexTo =
      this.availableColumnItems.findIndex((item) => this.displayedColumns[event.currentIndex] === item.value) +
      (this.actionOnMultiple ? 1 : 0);
    moveItemInArray(this.availableColumnItems, realIndexFrom, realIndexTo);
    this.updatePreference();
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public applyFilter(filter: IFilter): void {
    this.dataSource.setFilter(filter);
    this.activeFilters = [...this.dataSource.getAllFilters()];
    this.updatePreference();
  }

  public clearFilter(): void {
    this.globalSearchValue = "";
    this.dataSource.clearFilter();
    this.activeFilters = [];
    this.updatePreference();
  }

  public updateSearch(value?: string): void {
    this.globalSearchValue = value;
    this.dataSource.setFilter({
      data: value,
      propertyName: "globalSearch",
      dataType: DataType.COMBINED,
      combinedProperties: ["name", "firstname"],
    });
  }

  /**
   * Selection
   */
  public isAllSelected(): boolean {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  public masterToggle(): void {
    this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  /**
   * Preferences
   */
  public updatePreference(): void {
    if (this.responsiveService.isMobile) {
      return;
    } // don't save preferences on mobile

    this.preferenceService
      .update({
        context: PreferenceContext.QUESTIONNAIRES_PAGE,
        parameters: {
          filters: this.dataSource.getAllFilters(),
          availableColumnItems: this.availableColumnItems,
          itemsPerPage: this.paginator.pageSize,
        } as ITableParameter,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.getDisplayedColumns();
      });
  }

  public getDisplayedColumns(): void {
    this.displayedColumns = this.actionOnMultiple
      ? ["select", ...this.availableColumnItems.filter((item) => item.checked).map((item) => item.value)]
      : [...this.availableColumnItems.filter((item) => item.checked).map((item) => item.value)];
  }

  private async loadPreferences() {
    const parameters = await this.preferenceService.list(PreferenceContext.QUESTIONNAIRES_PAGE).pipe(first()).toPromise();
    if (parameters) {
      if (parameters.filters.length) {
        // Apply saved filters
        parameters.filters.forEach((filter: IFilter) => {
          this.applyFilter(filter);
        });
      }

      // Set page size from preference
      this.currentPageSize = parameters.itemsPerPage;

      // Restore column order + visibility + filter on available columns to avoid bug if a deleted column value is found in the preferences
      this.availableColumnItems = (parameters.availableColumnItems as Item[]).filter((col) =>
        this.availableColumns.find((el) => el === col.value)
      );
    }
    this.getDisplayedColumns();
  }

  public openQuestionnaireListHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.QuestionnairesPageHelp },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  public encodeToB64(str: string): string {
    this.sessionService.patientPageFrom = "/questionnaireList";
    return Base64Helper.utf8_to_b64(str);
  }

  public activateCheckBox(): void {
    this.actionOnMultiple = !this.actionOnMultiple;
  }

  public openDetails(element: IQuestionnaireList): void {
    this.qrService
      .getOneScoring(element.questionnaireIdentifier)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((scoring) => {
        this.responsiveDialog.open(QRDetailsComponent, {
          data: { QR: scoring.questionnaireResponse, scoring: scoring.scoring },
        });
      });
  }

  public onExportPDF(element: IQuestionnaireList): void {
    this.exportPdfInProgressMap[element.questionnaireIdentifier] = true;
    this.qrService
      .getOneScoring(element.questionnaireIdentifier)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(async (scoring) => {
        try {
          if (!this.showAnonymized) {
            this.patientService
              .getPatientUser(element.caremateIdentifier)
              .pipe(takeUntil(this.onDestroy$))
              .subscribe(
                (pu) => {
                  this.exportPDF(scoring, element.questionnaireIdentifier, pu);
                },
                (err) => {
                  this.exportPdfInProgressMap[element.questionnaireIdentifier] = false;
                  FileLogger.error("QuestionnaireListPageComponent", "Error while loading getPatientUser: ", err);
                }
              );
          } else {
            this.exportPDF(scoring, element.questionnaireIdentifier);
          }
        } catch (err) {
          this.exportPdfInProgressMap[element.questionnaireIdentifier] = false;
          this.snackBar.open(this.translateService.instant("api.errors.pdf-error"), "ok", { duration: 3000 });
          FileLogger.error("QuestionnaireListPageComponent", "Error while loading questionnaire: ", err);
        }
      });
  }
  private exportPDF(scoring: Scoring, questionnaireIdentifier: string, pu?: IPatientUser): void {
    const dialog = this.responsiveDialog.open(
      PdfTypeChoiceComponent,
      { data: { context: PDF_TYPE_CONTEXT.QUESTIONNAIRE, scoring: scoring, pu }, disableClose: true },
      {},
      {
        minWidth: "auto",
        width: "auto",
        maxWidth: "auto",

        minHeight: "auto",
        height: "auto",
        maxHeight: "auto",
      }
    );
    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.snackBar.open(this.translateService.instant("export.message"), "ok", {
          duration: 3000,
        });
      }
      this.exportPdfInProgressMap[questionnaireIdentifier] = false;
    });
  }

  public computeAnswerString(reference: string, pageNbr: number, groupResponse: GroupResponse[]): string {
    let answerString = "";
    const question = groupResponse?.[pageNbr]?.question?.find((q) => q.linkId === reference);
    if (question) {
      switch (question.inputType) {
        case INPUT_TYPE.NUMBER:
          answerString = question.answer?.[0]?.valueCoding?.display;
          if (question.answer?.[1]) {
            answerString += " " + question.answer?.[1]?.valueCoding?.display;
          }
          break;
        default:
          answerString = question.answer?.map((a) => a.valueCoding?.display ?? a.valueCoding?.code).join(", ");
      }
    }
    return answerString;
  }

  public computeAnswerCode(reference: string, pageNbr: number, groupResponse: GroupResponse[]): string {
    let answerCode: string;
    const question = groupResponse?.[pageNbr]?.question?.find((q) => q.linkId === reference);
    if (question?.inputType === INPUT_TYPE.CHECKBOXES) {
      answerCode = question?.answer?.map((a) => a.valueCoding?.code.toString()).join(", ");
    } else {
      const valueCoding = question?.answer?.[0]?.valueCoding;
      answerCode = isNaN(+valueCoding?.code) ? null : valueCoding.code;
    }
    return answerCode;
  }

  public getMergedScoringByCategory(scorings: QuestionnaireScoring[]): QuestionnaireScoring[][] {
    const allCategories = this.uniq(scorings.map((s) => s.identifier?.value));
    const o = {};
    allCategories.forEach((cat) => (o[cat] = []));
    scorings.forEach((s) => {
      o[s.identifier?.value].push(s);
    });
    return Object.keys(o).reduce((acc, el) => {
      acc.push(o[el]);
      return acc;
    }, [] as QuestionnaireScoring[][]);
  }

  private uniq(a: string[]) {
    return Array.from(new Set(a));
  }

  public async exportCSV(): Promise<void> {
    this.exportCsvInProgress = true;
    const msg = this.translateService.instant("export.message");
    this.snackBar.open(msg, "ok", { duration: 3000 });
    const sortedAndFilteredData = this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort);
    await this.questionnaireListCSVHelper.exportCSV(sortedAndFilteredData, true).catch((err) => {
      FileLogger.error("QuestionnaireListPage", "Error while exporting CSV", err, "none");
    });
    this.exportCsvInProgress = false;
    this.statService.createStatEvent("Export questionnaire responses to CSV");
  }

  public isAuthorizeShowAnonymized(): void {
    this.userService.isAuthorized("dashboard/anonymousQuestionnaires", "GET").subscribe((res) => {
      this.isAuthorizeShowAnonymousQr = res;
    });
  }

  public handleToggleShowAnonymizedClicked(): void {
    this.showAnonymized = !this.showAnonymized;
    this.init(this.fromDate, this.toDate);
  }

  openBottomSheet(): void {
    this._bottomSheet
      .open(MobileQuestionnaireListSettingsComponent, {
        autoFocus: false,
        data: { fromDate: this.fromDate, toDate: this.toDate, showAnonymized: this.showAnonymized },
      })
      .afterDismissed()
      .subscribe((data) => {
        if (!data) return; // on cancel
        this.fromDate = data.fromDate;
        this.toDate = data.toDate;
        this.showAnonymized = data.showAnonymized;
        this.init(this.fromDate, this.toDate);
      });
  }
}
